import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { gql } from '../lib/api';
import {
  IMPORT_HISTORY_QUERY,
  IMPORT_DAY_STATUSES_QUERY,
  REQUEST_CSV_UPLOAD_URL_MUTATION,
  STORES_QUERY,
} from '../graphql/queries';
import { useSelectedStore } from '../hooks/useSelectedStore';

interface Store {
  id: string;
  name: string;
}

interface ImportHistory {
  importId: string;
  date: string;
  fileName: string;
  csvType: string | null;
  storeId: string | null;
  rows: number | null;
  status: string;
  errors: string[] | null;
  createdAt: string;
}

interface ImportDayStatus {
  date: string;
  storeId: string;
  source: string;
  rows: number;
  complete: boolean;
  importedAt: string;
}

interface UploadUrlPayload {
  url: string;
  key: string;
  expiresInSec: number;
}

// Mac から取得するファイル名は NFD（濁点分離）の場合があり、リテラル比較が外れるため NFC 正規化する
const normName = (file: File): string => (file.name ?? '').normalize('NFC');

const isJournal = (file: File): boolean => {
  const n = normName(file);
  return n.includes('ジャーナル履歴') || n.toLowerCase().includes('journal');
};

const isKaikei = (file: File): boolean => {
  const n = normName(file);
  // ジャーナル履歴は別判定で先に拾うのでここでは除外（誤判定防止）
  if (isJournal(file)) return false;
  return n.toLowerCase().includes('kaikei') || n.includes('会計明細');
};

const isAirmateCost = (file: File): boolean => {
  const n = normName(file).toLowerCase();
  return n.includes('airmate_rawdata_daily_detail_cost') || n.includes('airmate-cost');
};

const isAirshift = (file: File): boolean => {
  const n = normName(file);
  return n.includes('シフトデータ') || n.toLowerCase().includes('airshift');
};

// Airシフトには店舗識別列がないため選択が必要。他はCSV内のAKRまたはレジIDで判定する。
const requiresStore = (file: File): boolean => isAirshift(file);

const jstToday = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

const monthDates = (month: string, today: string): string[] => {
  const [year, number] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, number, 0)).getUTCDate();
  return Array.from({ length: lastDay }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`)
    .filter((date) => date <= today)
    .reverse();
};

const weekday = (date: string): number => new Date(`${date}T00:00:00Z`).getUTCDay();

const csvLabel = (file: File): string => {
  if (isKaikei(file)) return '会計明細';
  if (isJournal(file)) return 'Airレジ ジャーナル履歴（過去分）';
  if (isAirmateCost(file)) return 'Airメイトコスト';
  if (isAirshift(file)) return 'Airシフト';
  return '不明';
};

export const UploadPage = () => {
  const qc = useQueryClient();
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQ.data?.stores ?? [];

  const [files, setFiles] = useState<File[]>([]);
  const [storeId, setStoreId] = useSelectedStore();
  const [progress, setProgress] = useState<string[]>([]);
  const todayJst = jstToday();
  const [selectedMonth, setSelectedMonth] = useState(todayJst.slice(0, 7));
  const days = monthDates(selectedMonth, todayJst);
  const monthFrom = `${selectedMonth}-01`;
  const monthTo = days[0] ?? monthFrom;

  const dayStatusQ = useQuery({
    queryKey: ['importDayStatuses', monthFrom, monthTo],
    queryFn: () => gql<{ importDayStatuses: ImportDayStatus[] }>(IMPORT_DAY_STATUSES_QUERY, { from: monthFrom, to: monthTo }),
    enabled: days.length > 0,
    refetchInterval: 15_000,
  });
  const dayStatuses = new Map((dayStatusQ.data?.importDayStatuses ?? []).map((item) => [
    `${item.date}#${item.storeId}#${item.source}`, item,
  ]));

  const today = new Date();
  const fromStr = new Date(today.getTime() - 30 * 86400000).toISOString().slice(0, 10);
  const toStr = todayJst;

  const historyQ = useQuery({
    queryKey: ['importHistory', fromStr, toStr],
    queryFn: () =>
      gql<{ importHistory: ImportHistory[] }>(IMPORT_HISTORY_QUERY, {
        from: fromStr,
        to: toStr,
      }),
    refetchInterval: 15_000,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await gql<{ requestCsvUploadUrl: UploadUrlPayload }>(
        REQUEST_CSV_UPLOAD_URL_MUTATION,
        {
          fileName: file.name,
          storeId: requiresStore(file) ? storeId || null : null,
          exportedAt: new Date(file.lastModified || Date.now()).toISOString(),
        },
      );
      const res = await fetch(data.requestCsvUploadUrl.url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'text/csv' },
      });
      if (!res.ok) throw new Error(`PUT failed: ${res.status}`);
      return data.requestCsvUploadUrl.key;
    },
    onSuccess: (key, file) => {
      setProgress((prev) => [...prev, `送信済み（解析中）: ${file.name} → ${key}`]);
      void qc.invalidateQueries({ queryKey: ['importHistory'] });
      void qc.invalidateQueries({ queryKey: ['importDayStatuses'] });
    },
    onError: (err, file) => {
      setProgress((prev) => [...prev, `NG: ${file.name} – ${(err as Error).message}`]);
    },
  });

  const handleUpload = async () => {
    setProgress([]);
    for (const f of files) {
      if (requiresStore(f) && !storeId) {
        setProgress((p) => [...p, `NG: ${f.name} – この CSV は店舗の指定が必要です`]);
        continue;
      }
      await uploadMutation.mutateAsync(f).catch(() => undefined);
    }
  };

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '1.4rem', marginBottom: '1rem' }}>CSV 取込</h1>

      <section style={card}>
        <h2 style={sectionTitle}>CSVのダウンロード手順</h2>
        <p style={pcNotice}>
          <strong>ダウンロードはPCから</strong>
          <span>Airレジの会計明細CSVはスマートフォンではダウンロードできません。Airメイトの日別コストもPCのWeb版で取得してください。</span>
        </p>
        <p style={muted}>下のリンクは各サービスの出力画面を直接開きます。メニューから開く場合の経路も記載しています。</p>
        <div className="upload-guides">
          <div className="upload-guide">
            <h3 style={downloadGuideTitle}><span className="upload-guide-number">01</span> Airレジ <span style={downloadGuideType}>会計明細</span></h3>
            <p style={downloadGuideRoute}>メニュー → 売上・分析 → 日別売上</p>
            <ol style={downloadSteps}>
              <li>画面上部で対象店舗に切り替える。</li>
              <li>集計対象を「日別」にし、対象日を含む年・月（通常は最新月）を確認して「表示する」を押す。</li>
              <li>「CSVデータをダウンロードする」を押し、表示された「会計明細」を選ぶ。</li>
              <li>もう一方の店舗に切り替えて、同じ操作を行う。</li>
            </ol>
            <div className="upload-guide-images upload-guide-images-airregi">
              <figure className="upload-guide-figure">
                <a href="/guides/airregi-daily-sales.png" target="_blank" rel="noopener noreferrer" aria-label="Airレジの日別売上画面の画像を拡大">
                  <img src="/guides/airregi-daily-sales.png" alt="Airレジの日別売上画面。店舗、集計対象の日別・年月・表示ボタン、右下のCSVデータをダウンロードするボタン" loading="lazy" />
                </a>
                <figcaption><strong>① 日別売上を表示</strong><span>画像をタップして拡大 ↗</span></figcaption>
              </figure>
              <figure className="upload-guide-figure">
                <a href="/guides/airregi-kaikei-menu.png" target="_blank" rel="noopener noreferrer" aria-label="Airレジの会計明細選択画面の画像を拡大">
                  <img src="/guides/airregi-kaikei-menu.png" alt="CSVデータをダウンロードするメニューを開き、会計明細を選ぶ画面" loading="lazy" />
                </a>
                <figcaption><strong>② 会計明細を選択</strong><span>画像をタップして拡大 ↗</span></figcaption>
              </figure>
            </div>
            <a href="https://airregi.jp/CLP//view/salesList/#/" target="_blank" rel="noopener noreferrer" style={downloadLink}>
              Airレジの日別売上を開く <span aria-hidden="true">↗</span>
            </a>
          </div>
          <div className="upload-guide">
            <h3 style={downloadGuideTitle}><span className="upload-guide-number">02</span> Airメイト <span style={downloadGuideType}>日別コスト</span></h3>
            <p style={downloadGuideRoute}>メニュー → データ入出力 → データ出力</p>
            <ol style={downloadSteps}>
              <li>期間に対象日を含む月（通常は最新月）を選ぶ。</li>
              <li>対象データで「日別コスト」を選ぶ。</li>
              <li>出力する店舗で対象の2店舗を選び、対象外の店舗は選択を外す。</li>
              <li>「ダウンロードする」を押す。</li>
            </ol>
            <div className="upload-guide-images">
              <figure className="upload-guide-figure">
                <a href="/guides/airmate-daily-cost.png" target="_blank" rel="noopener noreferrer" aria-label="Airメイトのデータ出力画面の画像を拡大">
                  <img src="/guides/airmate-daily-cost.png" alt="Airメイトのデータ出力画面。期間、日別コスト、出力する店舗、右下のダウンロードするボタン" loading="lazy" />
                </a>
                <figcaption><strong>日別コスト・対象店舗を選択</strong><span>画像をタップして拡大 ↗</span></figcaption>
              </figure>
            </div>
            <a href="https://airmate.jp/data_output/" target="_blank" rel="noopener noreferrer" style={downloadLink}>
              Airメイトのデータ出力を開く <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
        <p style={{ ...muted, marginBottom: 0 }}>営業終了後に両サービスのCSVを再ダウンロードし、下の「アップロード」から取り込んでください。</p>
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>アップロード</h2>
        <p style={hint}>
          以下の CSV をアップロードできます：
          <br />・<strong>Airレジ「会計明細」</strong>（売上 / 取引）— CSV のレジIDで店舗を自動判定
          <br />・<strong>Airレジ「ジャーナル履歴」</strong>（過去分の売上補完用）— CSV 内の店舗番号(AKR)で自動判定。<em>人数 / 滞在時間 / 席名 / カテゴリは取得不可</em>
          <br />・<strong>Airメイト「日次コスト明細」</strong>（原価／固定費）— CSV 内の AKR で店舗自動判定
          <br />・<strong>Airシフト「シフトデータ」</strong>（勤務実績）— 店舗選択が必要
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {files.some(requiresStore) && <label style={{ fontSize: '0.85rem' }}>
            店舗：
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              style={selectStyle}
            >
              <option value="">（選択してください）</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}（{s.id}）
                </option>
              ))}
            </select>
          </label>}
          <input
            type="file"
            multiple
            accept=".csv,text/csv"
            onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
          />
          <button
            type="button"
            disabled={files.length === 0 || uploadMutation.isPending}
            onClick={handleUpload}
            style={primaryBtn}
          >
            {uploadMutation.isPending ? '送信中…' : `${files.length} 件をアップロード`}
          </button>
        </div>

        {files.length > 0 && (
          <ul style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#495057' }}>
            {files.map((f) => (
              <li key={f.name}>
                {f.name} — {csvLabel(f)}
              </li>
            ))}
          </ul>
        )}

        {progress.length > 0 && (
          <ul style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>
            {progress.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        )}
      </section>

      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
          <h2 style={{ ...sectionTitle, marginBottom: 0 }}>日付・店舗別の取込完了</h2>
          <input type="month" value={selectedMonth} min="2026-09" max={todayJst.slice(0, 7)} onChange={(event) => {
            if (/^\d{4}-\d{2}$/.test(event.target.value)) setSelectedMonth(event.target.value);
          }} aria-label="表示月" style={selectStyle} />
        </div>
        <p style={muted}>各店舗のAirレジとAirメイトの両方を、営業終了後にダウンロード・アップロードすると○になります。営業時間中のCSVも取り込みますが、再アップロードまでは×です。月曜は定休日です。</p>
        {dayStatusQ.isLoading ? <p style={muted}>読込中…</p> : dayStatusQ.isError ? <p style={hint}>取込状況を取得できませんでした。</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...tableStyle, minWidth: 390 }}>
              <thead><tr><th style={th}>日付</th>{stores.map((store) => <th key={store.id} style={th}>{store.name}</th>)}</tr></thead>
              <tbody>{days.map((date) => {
                const closed = weekday(date) === 1;
                return <tr key={date}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{date.replaceAll('-', '/')}（{'日月火水木金土'[weekday(date)]}）</td>
                  {stores.map((store) => {
                    if (closed) return <td key={store.id} style={td}>定休日</td>;
                    const sales = dayStatuses.get(`${date}#${store.id}#airregi-kaikei`);
                    const cost = dayStatuses.get(`${date}#${store.id}#airmate-cost`);
                    const complete = Boolean(sales?.complete && cost?.complete);
                    return <td key={store.id} style={td}>
                      <strong style={{ color: complete ? '#2b8a3e' : '#aa0b56', fontSize: '1.1rem' }}>{complete ? '○' : '×'}</strong>
                      <span style={{ display: 'block', fontSize: '0.72rem', color: '#65605d' }}>売上 {sales?.complete ? '○' : '×'} ／ コスト {cost?.complete ? '○' : '×'}</span>
                    </td>;
                  })}
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}
      </section>

      <section style={card}>
        <h2 style={sectionTitle}>取込履歴（直近 30 日）</h2>
        <p style={muted}>履歴の「成功」はCSVの取込処理が成功したことを示します。営業日ごとの完了は上の表で確認してください。</p>
        {historyQ.isLoading ? (
          <p style={muted}>読込中…</p>
        ) : (historyQ.data?.importHistory.length ?? 0) === 0 ? (
          <p style={muted}>履歴はまだありません。</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>日付</th>
                <th style={th}>種別</th>
                <th style={th}>店舗</th>
                <th style={th}>件数</th>
                <th style={th}>状態</th>
                <th style={th}>ファイル</th>
              </tr>
            </thead>
            <tbody>
              {historyQ.data?.importHistory.map((h) => (
                <tr key={h.importId}>
                  <td style={td}>{h.date}</td>
                  <td style={td}>{h.csvType ?? '-'}</td>
                  <td style={td}>{h.storeId ?? '-'}</td>
                  <td style={td}>{h.rows ?? '-'}</td>
                  <td style={td}>
                    <StatusBadge status={h.status} />
                  </td>
                  <td style={{ ...td, fontSize: '0.75rem' }}>{h.fileName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, { bg: string; fg: string }> = {
    success: { bg: '#d3f9d8', fg: '#2b8a3e' },
    partial: { bg: '#fff3bf', fg: '#9c8005' },
    failed: { bg: '#ffe3e3', fg: '#aa0b56' },
  };
  const c = colors[status] ?? { bg: '#e9ecef', fg: '#495057' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.15rem 0.5rem',
        borderRadius: 999,
        fontSize: '0.75rem',
        background: c.bg,
        color: c.fg,
      }}
    >
      {status}
    </span>
  );
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: '0.75rem',
  color: '#495057',
};
const downloadLink: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1rem',
  width: '100%',
  boxSizing: 'border-box',
  padding: '0.65rem 0.85rem',
  border: '1px solid #d4d1cf',
  color: '#006d8d',
  textDecoration: 'none',
  fontSize: '0.85rem',
};
const pcNotice: React.CSSProperties = {
  display: 'flex',
  gap: '0.4rem 0.85rem',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  padding: '0.7rem 0.85rem',
  margin: '0 0 0.75rem',
  borderLeft: '3px solid #aa0b56',
  background: '#fdf4f8',
  color: '#54243b',
  fontSize: '0.85rem',
};
const downloadGuideTitle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.5rem',
  margin: 0,
  fontSize: '1rem',
  color: '#292e34',
};
const downloadGuideType: React.CSSProperties = { fontSize: '0.75rem', fontWeight: 400, color: '#65605d' };
const downloadGuideRoute: React.CSSProperties = { margin: '0.6rem 0 0', fontSize: '0.78rem', color: '#65605d' };
const downloadSteps: React.CSSProperties = {
  margin: '0.65rem 0 0.85rem',
  paddingLeft: '1.35rem',
  fontSize: '0.84rem',
  lineHeight: 1.65,
  color: '#33383d',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.9rem' };
const hint: React.CSSProperties = {
  background: '#fff5f5',
  border: '1px solid #ffe3e3',
  color: '#5c1414',
  padding: '0.6rem 0.8rem',
  borderRadius: 6,
  fontSize: '0.85rem',
  marginBottom: '0.75rem',
};
const selectStyle: React.CSSProperties = {
  marginLeft: '0.4rem',
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
};
const primaryBtn: React.CSSProperties = {
  padding: '0.5rem 0.9rem',
  borderRadius: 6,
  border: '1px solid #aa0b56',
  background: '#aa0b56',
  color: '#fff',
  cursor: 'pointer',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.5rem',
  borderBottom: '1px solid #f1f3f5',
};
